import { CustomerOrdersApiError } from './orders-api/errors'
import { normalizeCustomerOrder, normalizeCustomerOrderInvoice } from './orders-api/mappers'
import { buildOrdersListPath, normalizeRequiredOrderId } from './orders-api/query'
import { API_BASE, requestCustomerInvoiceBlob, requestCustomerOrdersJson } from './orders-api/request'
import type { CustomerOrder, CustomerOrderInvoice, CustomerOrderStatus } from './orders-api/types'

export type {
  CustomerOrder,
  CustomerOrderInvoice,
  CustomerOrderStatus,
  CustomerOrderPaymentState,
  CustomerOrderCurrency,
  CustomerOrderItem,
} from './orders-api/types'
export { CustomerOrdersApiError } from './orders-api/errors'

export const fetchCustomerOrders = async (
  token?: string | null,
  options: {
    status?: CustomerOrderStatus
    limit?: number
  } = {},
): Promise<CustomerOrder[]> => {
  const payload = await requestCustomerOrdersJson<{ orders?: unknown }>(buildOrdersListPath(options), token)
  const orders = Array.isArray(payload.orders) ? payload.orders : []
  return orders
    .filter((order): order is Record<string, unknown> => Boolean(order) && typeof order === 'object')
    .map((order) => normalizeCustomerOrder(order))
}

export const fetchCustomerOrderById = async (token: string | null | undefined, orderId: string): Promise<CustomerOrder> => {
  const normalizedId = normalizeRequiredOrderId(orderId)
  const payload = await requestCustomerOrdersJson<{ order?: unknown }>(
    `/orders/me/${encodeURIComponent(normalizedId)}`,
    token,
  )
  const order = payload.order
  if (!order || typeof order !== 'object') {
    throw new CustomerOrdersApiError(502, 'Risposta ordini non valida')
  }
  return normalizeCustomerOrder(order as Record<string, unknown>)
}

export const fetchCustomerOrderInvoices = async (
  token: string | null | undefined,
  orderId: string,
): Promise<CustomerOrderInvoice[]> => {
  const normalizedId = normalizeRequiredOrderId(orderId)
  const payload = await requestCustomerOrdersJson<{ invoices?: unknown }>(
    `/orders/me/${encodeURIComponent(normalizedId)}/invoices`,
    token,
  )
  const invoices = Array.isArray(payload.invoices) ? payload.invoices : []
  return invoices
    .filter((invoice): invoice is Record<string, unknown> => Boolean(invoice) && typeof invoice === 'object')
    .map((invoice) => normalizeCustomerOrderInvoice(invoice))
}

export const buildCustomerInvoiceDownloadUrl = (orderId: string, invoiceId: string) =>
  `${API_BASE}/orders/me/${encodeURIComponent(orderId)}/invoices/${encodeURIComponent(invoiceId)}/download`

export const openCustomerInvoicePdf = async (token: string | null | undefined, invoice: CustomerOrderInvoice): Promise<void> => {
  const downloadUrl = invoice.downloadUrl.trim()
  if (!downloadUrl || !downloadUrl.startsWith('/')) {
    throw new CustomerOrdersApiError(400, 'URL download fattura non valido')
  }

  if (typeof window === 'undefined') {
    throw new CustomerOrdersApiError(500, 'Apertura fattura disponibile solo nel browser')
  }

  // Open synchronously from click to avoid popup blockers.
  // Do not use noopener/noreferrer here: some browsers isolate the new context and cannot resolve blob URLs.
  const popup = window.open('', '_blank')
  if (!popup) {
    throw new CustomerOrdersApiError(
      400,
      'Popup bloccato dal browser. Consenti i popup per aprire la fattura.',
    )
  }

  try {
    popup.document.title = 'Apertura fattura...'
    popup.document.body.innerHTML = '<p style="font-family: sans-serif; padding: 16px;">Apro la fattura...</p>'

    const blob = await requestCustomerInvoiceBlob({ token, downloadUrl })
    if (blob.size === 0) {
      throw new CustomerOrdersApiError(502, 'La fattura ricevuta e vuota.')
    }

    const objectUrl = window.URL.createObjectURL(blob)

    if (popup.closed) {
      window.URL.revokeObjectURL(objectUrl)
      throw new CustomerOrdersApiError(400, 'La finestra della fattura e stata chiusa prima del caricamento.')
    }

    popup.location.replace(objectUrl)
    try {
      popup.opener = null
    } catch {
      // Best-effort hardening only.
    }
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000)
  } catch (error) {
    if (!popup.closed) {
      popup.document.title = 'Errore apertura fattura'
      popup.document.body.innerHTML =
        '<p style="font-family: sans-serif; padding: 16px;">Errore apertura fattura. Chiudi questa finestra e riprova.</p>'
    }
    throw error
  }
}
